import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import type { KpiPeriod } from "@/features/kpi/periodFilter";

import { apiGet } from "./client";

/**
 * GET /api/v2/kpi/site-metrikleri?period= — eski `frontend/legacy/kpi.html`
 * "Site Metrikleri" bölümünün (backend/app/core/api/site_metrikleri.py,
 * app/ga4/transformers.py `build_kpi_snapshot`) portu. Şema KASITLI GEVŞEK
 * (`.passthrough()`): KPI'lar heterojen (bazılarında `comparison`, bazılarında
 * `breakdown`, `bekleyen_iade_sayisi`'nde `extra_*` alanları var) — bkz.
 * api/ozet.ts aynı gevşeklik gerekçesi.
 */

export const siteKpiComparisonSchema = z
  .object({
    value: z.number().nullable(),
    direction: z.string().optional(),
    label: z.string().optional(),
    /** Fraksiyon (`value`, 0-1) DEĞİL — cur/prev'in KENDİSİ, DeltaBadge
     * "değer farkı" modu için (bkz. transformers.py _ga4_simple_kpi notu). */
    previous_value: z.number().nullable().optional(),
  })
  .passthrough();
export type SiteKpiComparison = z.infer<typeof siteKpiComparisonSchema>;

export const siteKpiBreakdownItemSchema = z
  .object({ name: z.string().nullable().optional() })
  .passthrough();
export type SiteKpiBreakdownItem = z.infer<typeof siteKpiBreakdownItemSchema>;

export const siteKpiSchema = z
  .object({
    id: z.string(),
    title: z.string(),
    value: z.number().nullable(),
    format: z.string(),
    source: z.string().optional(),
    status: z.string(),
    error: z.string().optional(),
    comparison: siteKpiComparisonSchema.optional(),
    breakdown: z.array(siteKpiBreakdownItemSchema).optional(),
  })
  .passthrough();
export type SiteKpi = z.infer<typeof siteKpiSchema>;

export const siteKpiCategorySchema = z.object({
  id: z.string(),
  title: z.string(),
  kpis: z.array(siteKpiSchema),
});
export type SiteKpiCategory = z.infer<typeof siteKpiCategorySchema>;

export const siteMetrikleriResponseSchema = z
  .object({
    generated_at: z.string().nullable(),
    period: z.string(),
    categories: z.array(siteKpiCategorySchema),
  })
  .passthrough();
export type SiteMetrikleriResponse = z.infer<typeof siteMetrikleriResponseSchema>;

/** Kuyruk dönemi (`useKpiPeriod`, today|7|30|90|"all") → backend `period` sözleşmesi
 * (today|7d|30d|all — backend `90` KABUL ETMEZ, bkz. site_metrikleri.py _GECERLI_PERIOD). */
export function kpiPeriodToApiPeriod(period: KpiPeriod): "today" | "7d" | "30d" | "all" {
  if (period === "today" || period === "all") return period;
  if (period === 7) return "7d";
  return "30d"; // 30 ve (KPI_PERIODS dışı ama tip olarak izinli) 90 → 30d varsayılanı
}

export function fetchSiteMetrikleri(period: KpiPeriod): Promise<SiteMetrikleriResponse> {
  const apiPeriod = kpiPeriodToApiPeriod(period);
  return apiGet(`/api/v2/kpi/site-metrikleri?period=${apiPeriod}`, siteMetrikleriResponseSchema);
}

export function siteMetrikleriQueryKey(period: KpiPeriod) {
  return ["kpi", "site-metrikleri", kpiPeriodToApiPeriod(period)] as const;
}

export function useSiteMetrikleri(period: KpiPeriod) {
  return useQuery({
    queryKey: siteMetrikleriQueryKey(period),
    queryFn: () => fetchSiteMetrikleri(period),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchInterval: 15 * 60_000,
  });
}
